import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState, useEffect, useRef } from 'react';
import { usePatchRequest } from '@/shared/hooks/requests.js';
import { NODE, FILE } from '@/shared/utils/urls.js';
import { toast } from 'sonner';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { useTranslation } from 'react-i18next';
import {
  MAX_FILENAME_LENGTH,
  MAX_FOLDER_NAME_LENGTH,
  filenameProblem,
  folderNameProblem,
  nameProblemMessage,
} from '@/shared/utils/filenames.js';
import { apiErrorMessage, parseApiError } from '@/shared/utils/apiError.js';
import { fieldErrors } from '@/shared/utils/fieldErrors.js';
import { FieldError } from '@/shared/ui/FieldError.jsx';

export const UpdateFolder = ({ open, onOpenChange, folder, setData }) => {
  const { t } = useTranslation('my-docs');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const updateFolder = usePatchRequest({ url: folder?.uuid ? NODE.replace('{node_uuid}', folder.uuid) : null });

  useEffect(() => {
    if (folder && open) {
      setName(folder.name || '');
      setError('');
    }
  }, [folder, open]);

  const handleUpdate = async (e) => {
    e?.preventDefault?.();
    if (updateFolder.loading) return;
    const trimmed = name.trim();
    const problem = folderNameProblem(trimmed);
    if (problem) {
      setError(nameProblemMessage(t, problem, 'folder'));
      return;
    }
    if (trimmed === folder?.name) {
      onOpenChange(false);
      return;
    }
    const res = await updateFolder.request({ data: { name: trimmed }, showError: false });
    if (res.success) {
      toast.success(t('updateFolder.success'));
      onOpenChange(false);
      setData();
      return;
    }
    const parsed = parseApiError(res.error);
    setError(
      parsed.code === 'invalid_folder_name'
        ? nameProblemMessage(t, parsed.params?.reason || 'invalid_chars', 'folder')
        : fieldErrors(parsed).name || apiErrorMessage(parsed),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('updateFolder.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleUpdate} className="space-y-1" noValidate>
          <Input
            data-testid="folder-rename-input"
            value={name}
            required
            maxLength={MAX_FOLDER_NAME_LENGTH}
            aria-label={t('updateFolder.placeholder')}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={error ? 'folder-rename-error' : undefined}
            onChange={(e) => { setName(e.target.value); setError(''); }}
            placeholder={t('updateFolder.placeholder')}
          />
          <FieldError id="folder-rename-error" message={error} />
        </form>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t('updateFolder.cancel')}</Button>
          </DialogClose>
          <Button
            onClick={handleUpdate}
            data-testid="folder-rename-save"
            disabled={updateFolder.loading || !name.trim()}
            className="text-white bg-blue-500 hover:bg-blue-600 border-none"
            style={{
              background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
            }}
          >
            {updateFolder.loading ? <LoadingSpinner color="#fff" /> : t('updateFolder.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RenameFile = ({ open, onOpenChange, file, setData }) => {
  const { t } = useTranslation('my-docs');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  // `name` is the full name, extension included.
  const currentName = file?.name || '';
  const renameFile = usePatchRequest({ url: file?.uuid ? FILE.replace('{file_uuid}', file.uuid) : null });

  useEffect(() => {
    if (open) {
      setName(currentName);
      setError('');
      // Select just the base name (not the extension) so renaming is easy but
      // the extension is preserved by default — like a file explorer.
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        const dot = currentName.lastIndexOf('.');
        el.setSelectionRange(0, dot > 0 ? dot : currentName.length);
      });
    }
  }, [open, currentName]);

  const handleRename = async () => {
    if (renameFile.loading) return;
    const trimmed = name.trim();
    if (trimmed === currentName) {
      onOpenChange(false);
      return;
    }
    const problem = filenameProblem(trimmed);
    if (problem) {
      setError(nameProblemMessage(t, problem, 'file'));
      return;
    }
    const { success, error: err } = await renameFile.request({
      data: { name: trimmed },
      showError: false,
    });
    if (success) {
      toast.success(t('rename.success') || 'Renamed');
      onOpenChange(false);
      setData?.();
      return;
    }
    const parsed = parseApiError(err);
    if (parsed.code === 'filename_conflict') {
      setError(t('rename.exists', { name: trimmed }) || `A file named "${trimmed}" already exists in this folder`);
    } else if (parsed.code === 'invalid_filename') {
      setError(nameProblemMessage(t, parsed.params?.reason || 'invalid_chars', 'file'));
    } else {
      setError(fieldErrors(parsed).name || apiErrorMessage(parsed));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('rename.title') || 'Rename file'}</DialogTitle>
        </DialogHeader>
        <Input
          ref={inputRef}
          data-testid="file-rename-input"
          value={name}
          required
          maxLength={MAX_FILENAME_LENGTH}
          aria-label={t('rename.placeholder') || 'New name'}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={error ? 'file-rename-error' : undefined}
          onChange={(e) => { setName(e.target.value); setError(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }}
          placeholder={t('rename.placeholder') || 'New name'}
        />
        <FieldError id="file-rename-error" message={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t('updateFolder.cancel')}</Button>
          </DialogClose>
          <Button
            onClick={handleRename}
            data-testid="file-rename-save"
            disabled={renameFile.loading || !name.trim()}
            className="text-white bg-blue-500 hover:bg-blue-600 border-none"
            style={{ background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)' }}
          >
            {renameFile.loading ? <LoadingSpinner color="#fff" /> : (t('rename.save') || 'Rename')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
